import {Component, OnInit} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {CashPointHttpService} from "../_services/cash-point-http.service";
import {Router} from "@angular/router";
import {AppRoutes} from "../../../enums/routes";
import {LocationRoutes} from "../_models/_enums/location-routes";
import {CashPointEntryFilters} from "../_models/cash-point-entry-filters";
import {CashPointEntryState} from "../_models/_enums/cash-point-entry-state";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {UrlHelper} from "../../../helpers/url-helper";
import {CashPointEntryDto} from "../_models/cash-point-entry-dto";
import {LocationDto} from "../../../models/location-dto";
import {LocationHttpService} from "../../../http-requests/location-http.service";
import {CurrencyPipe, DecimalPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {BasicPageComponent} from "../../../x-components/basic-page/basic-page.component";
import {LocationCashPointLinker} from "../_models/location-cash-point-linker";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from "@angular/cdk/drag-drop";
import {CurrencyInputComponent} from "../../../x-components/currency-input/currency-input.component";
import {CashModel} from "../_models/cash-model";
import {BookingType, BookingTypeLabels} from "../../../models/payment/booking-type";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {BusinessCaseTypeLabels} from "../_models/_enums/business-case-type";

@Component({
  selector: 'app-cash-points-assistant-closing',
  imports: [
    NgForOf,
    NgIf,
    BasicPageComponent,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    NgClass,
    CdkDragPreview,
    CurrencyInputComponent,
    CurrencyPipe,
    DecimalPipe,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './cash-points-assistant-closing.component.html',
  styleUrl: './cash-points-assistant-closing.component.scss'
})
export class CashPointsAssistantClosingComponent implements OnInit {
  start: Date | undefined;
  end: Date | undefined;
  aggregation: { [key: string]: CashPointEntryDto[] } | undefined;
  locations: LocationDto[] | undefined;
  locationCashPointLinker: LocationCashPointLinker[] | undefined;
  selectedLocationCashPointLinker: LocationCashPointLinker | undefined;
  cashModel: CashModel = new CashModel();
  protected readonly BookingTypeLabels = BookingTypeLabels;
  protected readonly BookingType = BookingType;
  protected readonly BusinessCaseTypeLabels = BusinessCaseTypeLabels;

  constructor(private cpHttpService: CashPointHttpService,
              private locationHttpService: LocationHttpService,
              private gas: GlobalAlertService,
              private router: Router) {
  }

  get drops(): string[] {
    if (!this.locationCashPointLinker) return [];
    return this.locationCashPointLinker.map(l => l.location.id);
  }

  ngOnInit(): void {
    let start = UrlHelper.QueryParams.start;
    let end = UrlHelper.QueryParams.end;

    if (!start || !end) {
      this.abort();
      return;
    }

    this.start = new Date(start);
    this.end = new Date(end);

    this.load();
  }

  load() {
    let filters = new CashPointEntryFilters();
    filters.states = [CashPointEntryState.PreparedForClosing];

    this.loadLocations(locations => {
      this.loadCashPointEntries(locations, filters);
    })
  }

  loadLocations(next: (locations: LocationDto[]) => void) {
    this.locationHttpService.list("", true)
      .withLoadingIndicator()
      .subscribe({
        next: values => {
          this.locations = values;
          next(this.locations);
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Daten aufgetreten.", AlertLevel.error);
          this.abort();
        }
      })
  }

  loadCashPointEntries(locations: LocationDto[], filters: CashPointEntryFilters | undefined = undefined) {
    this.cpHttpService.list(undefined, this.start, this.end, filters)
      .withLoadingIndicator()
      .subscribe({
        next: values => {
          this.aggregation = this.aggregatePerLocation(values);

          this.initialize();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Daten aufgetreten.", AlertLevel.error);
          this.abort();
        }
      })
  }

  aggregatePerLocation(cpes: CashPointEntryDto[]): { [p: string]: CashPointEntryDto[] } {
    cpes.forEach(cpe => CashPointEntryDto.init(cpe))
    return cpes
      .sort((a, b) => a.created.getTime() - b.created.getTime())
      .reduce((acc, cp) => {
        if (!cp.device.location) {
          acc["_"].push(cp);
          return acc;
        }

        if (!acc[cp.device.location]) acc[cp.device.location] = [];
        acc[cp.device.location].push(cp);
        return acc;
      }, {"_": []} as { [key: string]: CashPointEntryDto[] })
  }

  getLocationIds(): string[] {
    return Object.keys(this.aggregation ?? {});
  }

  aggregateLocations(locations: LocationDto[]): { [key: string]: LocationDto } {
    return locations
      .reduce((acc, loc) => {
        acc[loc.id] = loc;
        return acc;
      }, {} as { [key: string]: LocationDto })
  }

  getLocationById(id: string): LocationDto | undefined {
    return this.locations?.find(l => l.id === id);
  }

  initialize() {
    if (!this.locations || !this.aggregation) {
      this.abort();
      return;
    }

    let locationIds = Object.keys(this.aggregation);
    this.locationCashPointLinker = this.locations
      .filter(l => locationIds.includes(l.id) || l.active)
      .map(l => new LocationCashPointLinker(l, this.aggregation![l.id] ?? []))
      .sort((a, b) => a.location.order - b.location.order);

    // todo niko: remove after debug
    // this.selectedLocationCashPointLinker =
    // this.select(this.locationCashPointLinker.find(x => x.location.name == "Garten"))
  }

  drop(event: CdkDragDrop<CashPointEntryDto[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  select(location: LocationCashPointLinker | undefined) {
    this.cashModel = new CashModel();
    this.selectedLocationCashPointLinker = location;
    if (!location) {
      this.load()
      return;
    }

    // query all data
    location.cpes.forEach(cpe => {
      this.cpHttpService.getSummary(cpe.device.id, cpe.id)
        .withLoadingIndicator()
        .subscribe({
          next: values => {
            cpe.closing.summary = values;
            this.cashModel.closingModels.push(cpe.closing);

            this.cpHttpService.getBusinessCaseTypeSummary(cpe.device.id, cpe.id)
              .withLoadingIndicator()
              .subscribe({
                next: value => {
                  cpe.closing.businessCaseTypes = value;
                  // #836: decided by how the till was opened — its float after an Anfangsbestand, an empty drawer
                  // after a Geldtransit. The register and the direct close propose the same figure.
                  const skimTarget = CashPointEntryDto.skimTarget(cpe, value);
                  if (skimTarget !== undefined) {
                    cpe.closing.skimming_result = skimTarget.toCurrencyFraction();
                  }
                }
              })

            //this.cpHttpService.getLastClosed(cpe.device.id)
            //  .withLoadingIndicator()
            //  .subscribe({
            //    next: value => {
            //      if (value[BookingType.Cash] != undefined) {
            //        cpe.closing.skimming_result = value[BookingType.Cash];
            //      }
            //    }
            //  })
          },
          error: err => {
            console.error(err);
            this.gas.createAndShow("Fehler", "Fehler beim Laden der Daten aufgetreten.", AlertLevel.error);
            this.select(undefined);
          }
        })
    });
  }

  getBookingTypes(summary: { [p: number]: number }) {
    return Object.keys(summary)
      .map(k => parseInt(k))
      .filter(k => k <= 1); // 0: cash, 1: card
  }

  getBusinessCaseTypes(types: { [p: number]: number }) {
    return Object.keys(types).map(x => parseInt(x));
  }

  close() {
    if (this.cashModel.remaining != 0) return;
    if (!this.cashModel.hasUnfinished) return;

    if (!this.selectedLocationCashPointLinker || this.selectedLocationCashPointLinker.cpes.length == 0) {
      this.select(undefined);
      return;
    }

    let unfinished = this.selectedLocationCashPointLinker.cpes.filter(cpe => !cpe.closing.success);

    let count = 0;

    for (let cpe of unfinished) {
      let error = cpe.closing.validate();
      if (error) {
        this.gas.createAndShow("Fehler", error, AlertLevel.error);
        return;
      }

      let bcs = cpe.closing.toBusinessCaseInformation();

      count++;
      this.cpHttpService.close(cpe.device.id, cpe.id, bcs)
        .withLoadingIndicator()
        .subscribe({
          next: () => {
            cpe.closing.success = true;
          },
          error: (err: HttpErrorResponse) => {
            console.error(err);
            this.gas.createAndShow("Fehler", this.closeErrorMessage(err, cpe), AlertLevel.error);
          }
        })
    }
  }

  /**
   * Maps a failed cash-point close request to a German user-facing message. Branches on the structured
   * `ApiError.code` (see #F15 precedent in orders.component.ts) for known guard failures, falling back to
   * the existing generic message for anything else so unrelated failure modes are unaffected.
   */
  private closeErrorMessage(error: HttpErrorResponse, cpe: CashPointEntryDto): string {
    switch (error?.error?.code) {
      case "E_CASHPOINTENTRY_PENDING_BOOKINGS":
        return `Für dieses Gerät werden noch Buchungen verarbeitet. Bitte kurz warten und den Kassenschluss für '${cpe.device.name}' erneut versuchen.`;
      default:
        return `Fehler beim Schließen der Kasse '${cpe.device.name}' aufgetreten.`;
    }
  }

  abort() {
    this.router.navigate([`${AppRoutes.LocationModule}/${LocationRoutes.CashPoints}`]).then();
    this.locations = undefined;
    this.aggregation = undefined;
    this.locationCashPointLinker = undefined;
  }
}
